import {
  LatestOffersRequest,
  Offer,
  OfferResult,
  RatePlanOfferRequest,
  ShoppingCartItem,
  ShoppingCartValidationError
} from '../types'
import {calcOfferPriceSummary} from './offerUtils'
import {formatCurrency} from './utils'
import {getLogger} from './logging'
import {isBeforeDay, formatISODate, toPropertyDateTime} from './dateUtils'

interface LatestOffersAccumulator {
  requests: RatePlanOfferRequest[]
  requestKeys: Set<string>
}

interface CartValidationAccumulator {
  validationErrors: ShoppingCartValidationError[]
  unitGroupCounts: Record<string, number>
}

const logger = getLogger('cartUtils')

/**
 * Checks that shopping cart item's arrival date is not before today.
 * @param shoppingCartItem
 * @param today
 * @returns
 */
export function isValidArrivalDate(
  shoppingCartItem: ShoppingCartItem,
  today = new Date()
): boolean {
  const arrivalDateStr = shoppingCartItem.offer.arrival
  return !isBeforeDay(arrivalDateStr, today)
}

/**
 * Validates shopping cart items and removes any items with arrival date before today.
 * @param shoppingCartItems The shopping cart items
 * @returns The validated shopping cart items
 */
export function validateShoppingCartArrivalDates(
  shoppingCartItems: ShoppingCartItem[]
): ShoppingCartItem[] {
  // // remove items from shopping cart if arrival date is before today
  if (shoppingCartItems?.length > 0) {
    const today = new Date()
    return shoppingCartItems.filter(shoppingCartItem => {
      return isValidArrivalDate(shoppingCartItem, today)
    })
  } else {
    return []
  }
}

/**
 * Builds a request for the latest offers for shopping cart items.
 * @param shoppingCartItems The shopping cart items
 * @returns
 */
export function buildLatestOffersRequest(
  shoppingCartItems: ShoppingCartItem[],
  timeZone: string
): LatestOffersRequest {
  // create unique rate plan offer requests
  const {requests}: LatestOffersAccumulator = shoppingCartItems.reduce(
    (acc: LatestOffersAccumulator, cartItem: ShoppingCartItem) => {
      const ratePlanId = cartItem.offer?.ratePlan.id
      const arrival = formatISODate(
        toPropertyDateTime(cartItem.offer?.arrival, timeZone)
      )
      const departure = formatISODate(
        toPropertyDateTime(cartItem.offer?.departure, timeZone)
      )
      const guests = cartItem.guests
      const requestKey = `${ratePlanId}-${arrival}-${departure}-${guests}`

      if (!acc.requestKeys.has(requestKey)) {
        acc.requestKeys.add(requestKey)
        const request: RatePlanOfferRequest = {
          ratePlanId,
          arrival,
          departure,
          guests
        }
        acc.requests.push(request)
      }

      return acc
    },
    {requests: [], requestKeys: new Set()} as LatestOffersAccumulator
  )

  return {
    requests,
    lastUpdated: new Date().toISOString()
  }
}

/**
 * Creates a lookup map of latest offers, indexed by rate plan ID.
 * @param latestOffers The latest offers
 * @returns
 */
export function getLatestOffersByRatePlan(
  latestOffers: OfferResult[]
): Record<string, Offer> {
  // create a lookup map of latest offers
  const latestOfferByRatePlan = latestOffers.reduce(
    (acc: Record<string, Offer>, offerResult: OfferResult) => {
      // skip if no offers
      if (offerResult.offers.length === 0) {
        logger.error({
          message: 'Rate plan has no offers',
          data: {offerResult}
        })
        return acc
      }

      // warn if multiple offers
      if (offerResult.offers?.length > 1) {
        logger.warn({
          message: 'Rate plan has multiple offers',
          data: {count: offerResult.offers?.length, offerResult}
        })
      }

      const latestOffer = offerResult.offers[0]

      const ratePlanId = latestOffer.ratePlan.id
      acc[ratePlanId] = latestOffer
      return acc
    },
    {} as Record<string, Offer>
  )
  return latestOfferByRatePlan
}

export function getAvailabilityByUnitGroup(
  latestOffers: OfferResult[]
): Record<string, number> {
  // create a lookup map of latest offers
  const unitGroupAvailability = latestOffers.reduce(
    (acc: Record<string, number>, offerResult: OfferResult) => {
      // skip if no offers
      if (offerResult.offers.length === 0) {
        logger.error({
          message: 'Rate plan has no offers',
          data: {offerResult}
        })
        return acc
      }

      // warn if multiple offers
      if (offerResult.offers?.length > 1) {
        logger.warn({
          message: 'Rate plan has multiple offers',
          data: {count: offerResult.offers?.length, offerResult}
        })
      }

      const latestOffer = offerResult.offers[0]
      const unitGroupId = latestOffer.unitGroup?.id
      acc[unitGroupId] = latestOffer.availableUnits
      return acc
    },
    {} as Record<string, number>
  )
  return unitGroupAvailability
}

export function validateShoppingCart(
  shoppingCartItems: ShoppingCartItem[],
  latestOffers: OfferResult[]
): ShoppingCartValidationError[] {
  // create a lookup map of latest offers
  const latestOfferByRatePlan = getLatestOffersByRatePlan(latestOffers)
  const availabilityByUnitGroup = getAvailabilityByUnitGroup(latestOffers)

  const {validationErrors} = shoppingCartItems.reduce(
    (acc: CartValidationAccumulator, cartItem: ShoppingCartItem) => {
      const ratePlanId = cartItem.offer?.ratePlan.id
      const unitGroupId = cartItem.offer?.unitGroup.id
      const latestOffer = latestOfferByRatePlan[ratePlanId]

      // increment unit group count
      if (acc.unitGroupCounts[unitGroupId]) {
        acc.unitGroupCounts[unitGroupId] += 1
      } else {
        acc.unitGroupCounts[unitGroupId] = 1
      }

      // get unit group availability
      const totalCartUnits = acc.unitGroupCounts[unitGroupId] || 0
      const totalAvailableUnits = availabilityByUnitGroup[unitGroupId] || 0

      if (!isValidArrivalDate(cartItem)) {
        // remove item if arrival date is before today
        acc.validationErrors.push({
          id: cartItem.id,
          code: 'invalid_arrival_date',
          message:
            "The room's arrival date is before today.  This item will be removed from the cart."
        })
      } else if (latestOffer && latestOffer.availableUnits <= 0) {
        // no units available
        acc.validationErrors.push({
          id: cartItem.id,
          code: 'not_available',
          message:
            'There are no rooms available for the selected dates.  This item will be removed from the cart.'
        })
      } else if (
        latestOffer &&
        latestOffer.validationMessages &&
        latestOffer.validationMessages.length > 0
      ) {
        // no units available due to restrictions.  ex. rate plan is marked closed, etc.
        logger.warn({
          message: 'Rate plan has validation messages',
          data: {ratePlanId, validationMessages: latestOffer.validationMessages}
        })
        acc.validationErrors.push({
          id: cartItem.id,
          code: 'not_available',
          message:
            'There are no rooms available due to booking restrictions.  This item will be removed from the cart.'
        })
      } else if (totalCartUnits > totalAvailableUnits) {
        const availableUnits = availabilityByUnitGroup?.[unitGroupId] || 0

        // not enough available rooms
        acc.validationErrors.push({
          id: cartItem.id,
          code: 'not_enough_available',
          message: `There are not enough rooms available: ${
            availableUnits === 1 ? '1 room' : `${availableUnits} rooms`
          } available.  This item will be removed from the cart.`
        })
      } else if (
        latestOffer &&
        latestOffer.totalGrossAmount.amount >
          cartItem.offer.totalGrossAmount.amount
      ) {
        // price is higher
        const cartItemPriceSummary = calcOfferPriceSummary(
          cartItem.offer,
          cartItem.additionalServices
        )
        const latestOfferPriceSummary = calcOfferPriceSummary(
          latestOffer,
          cartItem.additionalServices
        )
        acc.validationErrors.push({
          id: cartItem.id,
          code: 'price_increase',
          message: `The room price has increased from ${formatCurrency(
            cartItemPriceSummary.total
          )} to ${formatCurrency(
            latestOfferPriceSummary.total
          )}.  This item will be updated.`
        })
      } else if (
        latestOffer &&
        latestOffer.totalGrossAmount.amount <
          cartItem.offer.totalGrossAmount.amount
      ) {
        // price is lower
        const cartItemPriceSummary = calcOfferPriceSummary(
          cartItem.offer,
          cartItem.additionalServices
        )
        const latestOfferPriceSummary = calcOfferPriceSummary(
          latestOffer,
          cartItem.additionalServices
        )
        acc.validationErrors.push({
          id: cartItem.id,
          code: 'price_decrease',
          message: `The room price has decreased from ${formatCurrency(
            cartItemPriceSummary.total
          )} to ${formatCurrency(
            latestOfferPriceSummary.total
          )}.  This item will be updated.`
        })
      }

      return acc
    },
    {validationErrors: [], unitGroupCounts: {}} as CartValidationAccumulator
  )

  return validationErrors
}

/**
 * Gets validation errors by shopping cart item ID.
 * @param validationErrors The list of validation errors
 * @returns
 */
export function getValidationErrorsByItem(
  validationErrors: ShoppingCartValidationError[]
): Record<string, ShoppingCartValidationError> {
  // create a lookup map of validation errors
  const validationErrorsByItem = validationErrors.reduce(
    (acc: Record<string, ShoppingCartValidationError>, error) => {
      const id = error.id
      acc[id] = error
      return acc
    },
    {} as Record<string, ShoppingCartValidationError>
  )
  return validationErrorsByItem
}

/**
 * Applies changes to the shopping cart based on validation errors and latest offers.
 * @param shoppingCartItems The shopping cart items
 * @param validationErrors The validation errors
 * @param latestOffers The latest offers
 * @returns
 */
export function applyShoppingCartChanges(
  shoppingCartItems: ShoppingCartItem[],
  validationErrors: ShoppingCartValidationError[],
  latestOffers: OfferResult[]
): ShoppingCartItem[] {
  // create a lookup map of latest offers
  const latestOfferByRatePlan = getLatestOffersByRatePlan(latestOffers)
  const validationErrorsByItem = getValidationErrorsByItem(validationErrors)

  const updatedShoppingCartItems = shoppingCartItems.reduce(
    (acc: ShoppingCartItem[], cartItem: ShoppingCartItem) => {
      const cartItemId = cartItem.id
      const ratePlanId = cartItem.offer?.ratePlan.id
      const validationError = validationErrorsByItem[cartItemId]
      const latestOffer = latestOfferByRatePlan[ratePlanId]

      if (validationError) {
        switch (validationError.code) {
          case 'invalid_arrival_date':
          case 'not_available':
          case 'not_enough_available':
            // remove item from cart
            logger.info({
              message: 'Removing item from cart',
              data: {validationError, cartItem}
            })
            break
          case 'price_increase':
          case 'price_decrease':
            if (latestOffer) {
              // update item in cart with latest offer
              logger.info({
                message: 'Updating item in cart',
                data: {validationError, cartItem, latestOffer}
              })
              const updatedCartItem = {
                ...cartItem,
                offer: latestOffer
              }
              acc.push(updatedCartItem)
            } else {
              logger.error({
                message: 'Latest offer not found for item',
                data: {validationError, cartItem, latestOffer}
              })
            }
            break
          default:
            logger.error({
              message: 'Unknown validation error code',
              data: {validationError, cartItem}
            })
            break
        }
      } else {
        // add item to cart unchanged if no validation error
        acc.push(cartItem)
      }

      return acc
    },
    [] as ShoppingCartItem[]
  )

  return updatedShoppingCartItems
}
